import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { RestockForm } from "@/components/RestockForm";
import { Minus, Plus, RotateCcw, Ruler, Star, Truck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Seo } from "@/components/Seo";
import { Loader } from "@/components/brand/Mascot";
import { ProductCard } from "@/components/ProductCard";
import { HangTags } from "@/components/brand/HangTags";
import { SizeGuide } from "@/components/SizeGuide";
import { useDropGate } from "@/lib/access";
import { useCart } from "@/context/CartContext";
import { getProduct, getRelated } from "@/lib/api";
import { abs, breadcrumb, cad, cadShort, imagesFor, totalStock } from "@/lib/site";
import NotFound from "@/pages/NotFound";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { useShareReward } from "@/context/AuthContext";

const ShareProduct = ({ p }) => {
  const reward = useShareReward();
  const share = async () => {
    const url = abs(`/product/${p.slug}`);
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      try { await navigator.share({ title: p.name, text: `${p.name} by the suspects`, url }); reward("product", p.slug); return; } catch (e) { if (e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied."); reward("product", p.slug); } catch { toast(url); }
  };
  return <button onClick={share} className="label inline-flex items-center gap-1.5 font-bold hover:text-ink/60" data-testid="product-share"><Share2 size={13} /> Share</button>;
};

const Gallery = ({ images }) => {
  const [active, setActive] = useState(0);
  const im = images[active];
  return (
    <div className="flex flex-col-reverse gap-4 sm:flex-row">
      <div className="flex gap-3 sm:flex-col">
        {images.map((m, i) => (
          <button key={m.src} onClick={() => setActive(i)} aria-label={`Show image ${i + 1}`} aria-pressed={active === i} className={`tile h-20 w-20 overflow-hidden transition-opacity ${active === i ? "ring-1 ring-ink" : "opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${i}`}>
            <img src={m.src} alt="" className={`h-full w-full object-cover ${i === 0 ? "mix-blend-multiply" : "photo"}`} loading="lazy" />
          </button>
        ))}
      </div>
      <div className="tile relative flex-1 overflow-hidden rounded-[24px]">
        <img key={im.src} src={im.src} alt={im.alt} className={`aspect-square w-full object-cover animate-in fade-in duration-500 ${active === 0 ? "mix-blend-multiply" : "photo"}`} data-testid="gallery-main-image" />
      </div>
    </div>
  );
};

const Rating = ({ value, count }) => (
  <div className="flex items-center gap-2" data-testid="product-rating" aria-label={`Rated ${value} out of 5 from ${count} reviews`}>
    <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} strokeWidth={1.5} className={i <= Math.round(value) ? "fill-ink text-ink" : "text-ink/30"} />)}</div>
    <span className="text-[13px] text-ink/60">{value} ({count} reviews)</span>
  </div>
);

const INFO = (p) => [
  ["details", "Details", <><p>{p.description}</p><ul className="mt-4 list-disc space-y-1 pl-5">{p.details.map((d) => <li key={d}>{d}</li>)}</ul></>],
  ["fit", "Fit", <p>{p.fit} Unisex sizing XS to XXL. Between sizes? Size down for a regular fit, up for extra slouch.</p>],
  ["shipping", "Shipping", <p>Free shipping in Canada on orders over C$100, otherwise a flat C$10. Ships from Toronto in 1 to 3 business days. We ship across Canada and to the USA.</p>],
  ["returns", "Returns", <p>30-day returns on unworn items with tags attached. Final sale on limited drop pieces marked as such. Exchanges are free within Canada.</p>],
];

const productLd = (p) => ({
  "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.slug, description: p.description,
  image: p.images.map((i) => abs(i.src)), brand: { "@type": "Brand", name: "the suspects" }, category: p.category,
  color: p.colors.map((c) => c.name).join(", "),
  aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count },
  offers: { "@type": "Offer", url: abs(`/product/${p.slug}`), priceCurrency: "CAD", price: p.price.toFixed(2), itemCondition: "https://schema.org/NewCondition", availability: totalStock(p) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", seller: { "@type": "Organization", name: "the suspects" } },
});

const Buy = ({ p, openTab, color, setColor, onSizeGuide }) => {
  const { addItem } = useCart();
  const gate = useDropGate(p.drop_slug);
  const locked = gate.loading || gate.gated;
  const oneSize = "OS" in p.sizes;
  const [params] = useSearchParams();
  const [size, setSize] = useState(oneSize ? "OS" : (p.sizes[params.get("size")] > 0 ? params.get("size") : null));
  const [notify, setNotify] = useState(p.sizes[params.get("size")] === 0 ? params.get("size") : null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const stock = size ? p.sizes[size] : 0;

  const add = async () => {
    setBusy(true);
    await addItem(p, { color, size, qty, image: imagesFor(p, color)[0].src });
    setBusy(false);
  };

  return (
    <div className="mt-8 space-y-8 border-t border-ink/10 pt-8">
      <fieldset>
        <legend className="label">Colour: <span className="font-bold" data-testid="selected-color">{color}</span></legend>
        <div className="mt-4 flex gap-3">
          {p.colors.map((c) => (
            <button key={c.name} onClick={() => setColor(c.name)} aria-label={c.name} aria-pressed={color === c.name} className={`h-8 w-8 rounded-full ring-1 ring-ink/25 ring-offset-[3px] ring-offset-cloud transition-shadow ${color === c.name ? "!ring-ink !ring-[1.5px]" : ""}`} style={{ background: c.hex }} data-testid={`product-color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>
      </fieldset>
      {!oneSize && (
        <fieldset>
          <div className="flex justify-between">
            <legend className="label">Size{size && <>: <span className="font-bold" data-testid="selected-size">{size}</span></>}</legend>
            <button onClick={onSizeGuide} className="label text-ink/55 underline underline-offset-4" data-testid="size-guide-inline">Size guide</button>
          </div>
          <div className="mt-4 grid grid-cols-6 gap-2">
            {Object.entries(p.sizes).map(([s, n]) => (
              <button key={s} onClick={() => (n === 0 ? setNotify(s) : (setSize(s), setQty(1), setNotify(null)))} aria-pressed={size === s} aria-label={n === 0 ? `${s}, sold out, notify me` : s} className={`flex h-12 flex-col items-center justify-center rounded-[10px] border font-mono text-xs transition-colors ${n === 0 ? `border-dashed border-ink/30 text-ink/40 hover:border-ink ${notify === s ? "bg-sky/40" : ""}` : size === s ? "border-ink bg-ink text-cloud" : "border-ink/40 hover:border-ink"}`} data-testid={`product-size-chip-${s}`}>
                <span className={n === 0 ? "line-through" : ""}>{s}</span>{n === 0 && <span className="text-[8px] font-bold uppercase tracking-wide text-ink/60">Notify</span>}
              </button>
            ))}
          </div>
          {notify && <div className="mt-4"><RestockForm p={p} color={color} size={notify} onClose={() => setNotify(null)} /></div>}
          {totalStock(p) === 0 && !notify && <button onClick={() => setNotify(Object.keys(p.sizes)[0])} className="link-u mt-4" data-testid="product-notify-me">Sold out · Notify me</button>}
          {size && stock <= 3 && <p className="mt-3 text-[13px] text-alert" data-testid="low-stock-note">Only {stock} left in {size}.</p>}
        </fieldset>
      )}
      {gate.drop && gate.gated && (
        <div className="border-[1.5px] border-ink bg-sky px-4 py-3" data-testid="product-preview-note">
          <p className="label font-bold">Preview · Drop {gate.drop.number} lands {gate.drop.release_label}</p>
          <p className="mt-1 text-[13px]">Sus Crew gets in early. <Link to={`/drops/${gate.drop.slug}`} className="underline underline-offset-4" data-testid="product-preview-drop-link">See the drop</Link></p>
        </div>
      )}
      {gate.drop && gate.access?.valid && !gate.gated && <p className="sticker bg-pink" data-testid="product-early-access">Crew early access: you're in</p>}
      <div className="flex gap-3">
        <div className="flex items-center border border-ink/40">
          <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-full px-3.5 transition-colors hover:bg-ink/5" aria-label="Decrease quantity" data-testid="qty-decrease"><Minus size={14} /></button>
          <span className="w-8 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
          <button onClick={() => setQty(Math.min(stock || 1, qty + 1))} disabled={!size} className="h-full px-3.5 transition-colors hover:bg-ink/5 disabled:opacity-30" aria-label="Increase quantity" data-testid="qty-increase"><Plus size={14} /></button>
        </div>
        <button onClick={add} disabled={locked || !size || stock === 0 || busy} className="btn btn-lime h-14 flex-1" data-testid="add-to-cart-button">
          {gate.gated ? "Preview only" : busy ? "Checking stock…" : !size ? "Select a size" : stock === 0 ? "Sold out" : "Add to cart"}
        </button>
      </div>
      <ul className="flex flex-wrap gap-x-8 gap-y-3">
        {[[Ruler, "Size guide", "fit"], [Truck, "Shipping", "shipping"], [RotateCcw, "Easy returns", "returns"]].map(([Icon, l, t]) => (
          <li key={t}><button onClick={() => (t === "fit" ? onSizeGuide() : openTab(t))} className="label flex items-center gap-2 text-ink/70 transition-colors hover:text-ink" data-testid={`product-link-${t}`}><Icon size={14} strokeWidth={1.5} /> {l}</button></li>
        ))}
      </ul>
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [tab, setTab] = useState("details");
  const [search] = useSearchParams();
  const [color, setColor] = useState(search.get("color"));
  const [guide, setGuide] = useState(false);
  const tabsRef = useRef(null);

  useEffect(() => {
    setP(undefined);
    setTab("details");
    setColor(search.get("color"));
    getProduct(slug).then(setP).catch(() => setP(null));
    getRelated(slug).then(setRelated).catch(() => setRelated([]));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTab = (t) => {
    setTab(t);
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (p === undefined) return <Loader label="Loading" />;
  if (p === null) return <NotFound />;

  const cat = p.category[0].toUpperCase() + p.category.slice(1);
  const colorName = p.colors.find((c) => c.name === color)?.name || p.colors[0].name;
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-8 sm:px-10" data-testid="product-page">
      <Seo title={`${p.name}, ${cad(p.price)} CAD`} description={`${p.name} by the suspects (Toronto streetwear). ${p.description} ${cad(p.price)} CAD.`} path={`/product/${p.slug}`} image={p.images[0].src} type="product"
        jsonLd={[productLd(p), breadcrumb([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }, { name: cat, path: `/shop?category=${p.category}` }, { name: p.name, path: `/product/${p.slug}` }])]} />
      <nav aria-label="Breadcrumb" className="label text-ink/45">
        <ol className="flex flex-wrap gap-2"><li><Link to="/" className="hover:text-ink">Home</Link> /</li><li><Link to="/shop" className="hover:text-ink">Shop</Link> /</li><li><Link to={`/shop?category=${p.category}`} className="hover:text-ink">{cat}</Link> /</li><li aria-current="page" className="text-ink">{p.name}</li></ol>
      </nav>
      <div className="mt-8 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7"><Gallery key={colorName} images={imagesFor(p, colorName)} /></div>
        <div className="lg:col-span-5 lg:sticky lg:top-40 lg:self-start">
          <h1 className="display text-[clamp(2.75rem,4.5vw,4.5rem)]" data-testid="product-name">{p.name}</h1>
          <p className="mt-4 text-lg" data-testid="product-price">{cadShort(p.price)}</p>
          <div className="mt-3 flex flex-wrap items-center gap-5"><Rating value={p.rating} count={p.review_count} /><ShareProduct p={p} /></div>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-ink/70">{p.description}</p>
          <Buy key={p.slug} p={p} openTab={openTab} color={colorName} setColor={setColor} onSizeGuide={() => setGuide(true)} />
          <SizeGuide category={p.category} open={guide} onOpenChange={setGuide} />
        </div>
      </div>
      <div ref={tabsRef} className="mt-24 scroll-mt-40">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto w-full justify-start gap-8 overflow-x-auto rounded-none border-b border-ink/10 bg-transparent p-0">
            {INFO(p).map(([v, l]) => <TabsTrigger key={v} value={v} className="label -mb-px rounded-none border-b-[1.5px] border-transparent px-0 pb-4 font-bold text-ink/45 data-[state=active]:border-ink data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:shadow-none" data-testid={`product-tab-${v}`}>{l}</TabsTrigger>)}
          </TabsList>
          {INFO(p).map(([v, , c]) => <TabsContent key={v} value={v} className="max-w-2xl pt-8 text-[15px] leading-relaxed text-ink/75" data-testid={`product-tab-content-${v}`}>{c}</TabsContent>)}
        </Tabs>
      </div>
      <section className="mt-24 grid items-center gap-10 border-y border-ink/10 py-14 md:grid-cols-12" aria-labelledby="tagged-title">
        <div className="md:col-span-7 lg:col-span-8">
          <p className="label text-ink/50">In the box</p>
          <h2 id="tagged-title" className="display mt-4 text-[clamp(2.5rem,5vw,4.5rem)]">Tagged certified sus</h2>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-ink/70">Designed in Toronto, printed in small batches. Every piece ships with our hang tags, so you know it's the real suspect.</p>
        </div>
        <div className="md:col-span-5 lg:col-span-4"><HangTags className="scale-[0.85]" /></div>
      </section>
      {related.length > 0 && (
        <section className="mt-28" aria-labelledby="related-title">
          <h2 id="related-title" className="display text-[clamp(2.5rem,5vw,4.5rem)]">You may also like</h2>
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-testid="related-grid">{related.map((r, i) => <ProductCard key={r.slug} product={r} index={i + 4} />)}</div>
        </section>
      )}
    </div>
  );
}
